/**
 * Product link click tracking for blog articles.
 *
 * Publishes 'nt_blog_product_click' to the Shopify Analytics Layer for every click on a
 * Product link inside the Article content ([data-nt-blog-article]).
 *
 * Markup contract:
 * - data-nt-link-position on a link or its container: 'product_card' or 'cta'; links without
 *   one report 'inline'
 * - data-nt-link-text on a link or its card container: the product title reported as link_text
 *
 * Loaded on article pages only (layout/theme.liquid). Not built through src/: rollup treats
 * every file in src/ as an entry point, so edit this file directly.
 * Test: src/scripts/nt-blog-product-click-tracking.test.js
 */

(() => {
  if (window.__ntBlogProductClickTracking) return;
  window.__ntBlogProductClickTracking = true;

  const EVENT_NAME = 'nt_blog_product_click';
  const TEXT_MAX = 100;
  // Article content links to products as absolute naturtreuhub.com URLs, also on the AT and CH domains
  const SHOP_HOST = /^(www\.)?naturtreu\.(de|at|ch)$/;

  // --- Consent: same category as the GA4 tags (analytics), fail closed ---------------------
  let analyticsAllowed = false;

  /** @returns {boolean} whether the Customer Privacy API answered */
  function readConsent() {
    analyticsAllowed = false;
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy) return false;
      analyticsAllowed = !!privacy.analyticsProcessingAllowed();
      return true;
    } catch (error) {
      return false;
    }
  }

  function canPublish() {
    const shopify = window.Shopify;
    return !!(analyticsAllowed && shopify && shopify.analytics && typeof shopify.analytics.publish === 'function');
  }

  // --- Payload ------------------------------------------------------------------------------
  function normalize(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  // Cards with several links carry the product title once on the card container
  function linkText(link) {
    const image = link.querySelector('img[alt]');
    const titled = link.closest('[data-nt-link-text]');
    const text = [
      titled && titled.getAttribute('data-nt-link-text'),
      link.getAttribute('aria-label'),
      link.textContent,
      image && image.getAttribute('alt'),
    ]
      .map(normalize)
      .find(Boolean);
    return (text || '').slice(0, TEXT_MAX);
  }

  // --- Held clicks ------------------------------------------------------------------------
  // Clicks are held in-page until they can be published. A held click that navigates away
  // is lost; there is no storage across page loads.
  const pending = [];

  function flushPending() {
    while (pending.length && canPublish()) {
      try {
        window.Shopify.analytics.publish(EVENT_NAME, pending[0]);
      } catch (error) {
        return; // stays held until the next consent change
      }
      pending.shift();
    }
  }

  function onConsentChange() {
    const answered = readConsent();
    flushPending();
    return answered;
  }

  // visitorConsentCollected fires on the visitor's decision, so an answered "not allowed" is a
  // denial; without a readable API the clicks stay held
  function onConsentCollected() {
    if (onConsentChange() && !analyticsAllowed) pending.length = 0;
  }

  // --- Clicks -------------------------------------------------------------------------------
  // A middle-click (open in new tab) fires auxclick with button 1 instead of click
  function onClick(event) {
    if (event.type === 'auxclick' && event.button !== 1) return;
    const link = event.target.closest && event.target.closest('a[href*="/products/"]');
    if (!link || !link.closest('[data-nt-blog-article]')) return;

    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin && !SHOP_HOST.test(url.hostname)) return;
    const positionEl = link.closest('[data-nt-link-position]');

    pending.push({
      blog_path: location.pathname,
      link_url: url.origin + url.pathname,
      link_text: linkText(link),
      link_position: positionEl ? positionEl.getAttribute('data-nt-link-position') : 'inline',
    });
    flushPending();
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);

  // Consent is read from the Customer Privacy API, independent of the CMP's event payload.
  // The loadFeatures callback is async.
  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) onConsentChange();
    });
  }
  document.addEventListener('visitorConsentCollected', onConsentCollected);
})();
